<div class="find-leads-shell">
  <!-- Page Heading Section -->
  <ng-container *ngTemplateOutlet="pageHeaderTemplate"></ng-container>

  @if (isSearchPage) {
    <!-- SearchPage Template -->
    <ng-container *ngTemplateOutlet="searchPageTemplate"></ng-container>
  } @else {
    <!-- Tabs Section -->
    <ng-container *ngTemplateOutlet="tabsTemplate"></ng-container>

    <!-- Sub Tabs Section -->
    <ng-container *ngTemplateOutlet="subTabsTemplate"></ng-container>
  }

  <!-- Search Results Section -->
  @if (
    selectedTab?.value !== LeadTabs.VLF || (isVlfOptedIn && !isVlfStatusLoading)
  ) {
    <ng-container *ngTemplateOutlet="searchResultsTemplate"></ng-container>
  }
</div>

<ng-template #pageHeaderTemplate>
  <div class="page-header">
    <div class="page-heading">
      <div class="page-title">Find Leads</div>

      <div class="page-subtitle">
        Browse and search for businesses across the Data Hub
      </div>
    </div>

    @if (!isSearchPage) {
      <div class="header-search" id="tour-find-leads-search">
        <app-search-input
          [placeholder]="'Search by business name...'"
          [searchQuery]="searchQuery"
          [disableSearch]="isSearchResultsLoading"
          (searchQueryChange)="onSearchQueryChange($event)"
        ></app-search-input>
      </div>
    }
  </div>
</ng-template>

<ng-template #tabsTemplate>
  <div class="tabs-wrapper" id="tour-find-leads-controls">
    <div class="tabs-container">
      <div class="tabs-left">
        <div class="find-in-label">Find in:</div>

        <div class="tabs-list">
          @for (tab of config; track tab.value) {
            <button
              type="button"
              class="tab-button"
              [class.active]="selectedTab?.value === tab.value"
              [attr.aria-selected]="selectedTab?.value === tab.value"
              [disabled]="isSearchResultsLoading"
              (click)="onTabChange(tab)"
              [attr.title]="tab.label"
            >
              {{ tab.label }}
            </button>
          }
        </div>
      </div>

      @if (selectedTab?.value !== LeadTabs.DATA_AXLE) {
        <div class="results-badge">Total results: {{ totalResults }}</div>
      }
    </div>
  </div>
</ng-template>

<ng-template #subTabsTemplate>
  @if (subTabs.length > 0) {
    <div class="sub-tabs-wrapper" id="tour-find-leads-subnav">
      <div class="sub-tabs-container">
        <div class="sub-tabs-left">
          <div class="sub-tabs-list">
            @for (subTab of subTabs; track subTab.value) {
              @if (checkPermission(subTab.permissionCode)) {
                <div class="sub-tab-item">
                  <button
                    type="button"
                    class="sub-tab-button"
                    [class.active]="selectedSubTab?.value === subTab.value"
                    [disabled]="
                      advanceFilterCount > 0 || isSearchResultsLoading
                    "
                    (click)="onSubTabChange(subTab)"
                  >
                    <span>{{ subTab.label }}</span>

                    @if (subTab.showCount && deeprootsCount) {
                      <span class="sub-tab-count">
                        {{ getDeepRootsCount(subTab.value) | count }}
                      </span>
                    }
                  </button>

                  @if (advanceFilterCount > 0) {
                    <div class="custom-tooltip">
                      Clear Advanced Filters to access sub tabs.
                    </div>
                  }
                </div>
              }
            }
          </div>
        </div>
        @if (selectedSubTab?.filters?.length) {
          <div>
            @for (filter of selectedSubTab?.filters ?? []; track filter) {
              @if (filter === LeadFilterTypes.MATCHED_CRITERIA) {
                <ng-container
                  *ngTemplateOutlet="matchedCriteriaTemplate"
                ></ng-container>
              }
              @if (filter === LeadFilterTypes.PRE_LENDING_FORM) {
                <ng-container
                  *ngTemplateOutlet="preLendingFormTemplate"
                ></ng-container>
              }
              @if (filter === LeadFilterTypes.INQUIRY_SUBMISSIONS) {
                <ng-container
                  *ngTemplateOutlet="inquirySubmissionsTemplate"
                ></ng-container>
              }
            }
          </div>
        }
        @if (selectedTab?.showAddBusinessButton) {
          <app-add-business-record
            class="subnav-action"
            [variant]="'compact'"
            [disableAddBusinessButton]="isSearchResultsLoading"
            (addedBusiness)="onCustomBusinessAdded()"
          ></app-add-business-record>
        }
        @if (selectedTab?.showAdvancedFilters) {
          <button
            type="button"
            class="advanced-filters-button"
            [disabled]="isSearchResultsLoading"
            [class.active]="isAdvancedFilterVisible"
            (click)="toggleAdvancedFilters()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path
                d="M3.9 54.9C10.5 40.9 24.5 32 40 32l432 0c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9 320 448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6l0-79.1L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z"
              />
            </svg>
            <span>Advanced Filters</span>
            <span class="button-arrow" [class.open]="isAdvancedFilterVisible"
              >▾</span
            >
            @if (advanceFilterCount > 0) {
              <span class="filter-count-badge">{{ advanceFilterCount }}</span>
            }
          </button>
        }
      </div>
    </div>
  }
  @if (selectedTab?.value === LeadTabs.WNCSBI) {
    <div
      class="advanced-search-wrapper"
      [class.hidden]="!isAdvancedFilterVisible"
    >
      <app-search-wncsbi
        [showQuickFilters]="false"
        [showOutreachQuickFilter]="false"
        [showLoanQuickFilter]="false"
        [showAdvancedToggle]="false"
        [advancedSearchVisible]="true"
        (filterCountChanged)="filterCountChanged($event)"
        (reloadAllData)="onResetAdvanceSearch()"
        (searchSubmitted)="onWNCSBIAdvanceSearch($event, 'CRF')"
      >
      </app-search-wncsbi>
    </div>
  }
  @if (selectedTab?.value === LeadTabs.DATA_AXLE) {
    <div>
      <app-search-data-axle
        (searchSubmitted)="onDataAxleAdvancedSearch($event, 'DATA_AXLE')"
        (filterCountChanged)="filterCountChanged($event)"
        (reloadAllData)="onResetAdvanceSearch()"
      ></app-search-data-axle>
    </div>
  }

  @if (selectedTab?.value === LeadTabs.VLF) {
    @if (isVlfStatusLoading) {
      <div class="vlf-programme-card vlf-skeleton-card">
        <div class="vlf-skeleton-header">
          <div class="vlf-skeleton-icon"></div>
          <div class="vlf-skeleton-lines">
            <div class="vlf-skeleton-line title"></div>
            <div class="vlf-skeleton-line subtitle"></div>
          </div>
        </div>
      </div>
    } @else {
      <div class="vlf-programme-card">
        @if (!isVlfOptedIn) {
          <div class="vlf-header">
            <div class="vlf-icon">
              <app-svg-icon
                iconName="vlf.svg"
                width="36px"
                height="36px"
              ></app-svg-icon>
            </div>
            <div>
              <h3>Veteran Loan Fund (VLF) Programme</h3>
              <p>Direct Pre-Screened Small Business Lead Generation Channel</p>
            </div>
          </div>

          <div class="vlf-body">
            <h5>What is the VLF Programme?</h5>
            <p>
              The Veteran Loan Fund (VLF) is an automated lead distribution
              network that delivers high-intent small business leads directly to
              participating lenders and CDFIs.
            </p>

            <h5>Programme Conditions & How It Works:</h5>
            <ul class="vlf-conditions-list">
              <li>
                <strong>First-Come, First-Assigned:</strong> Leads in your
                location are broadcasted to all participating organizations. The
                first organization to claim a lead secures exclusive assignment.
              </li>
              <li>
                <strong>Funding Time Frame:</strong> Your organization has 7
                business days to fund claimed leads and submit weekly status
                updates.
              </li>
              <li>
                <strong>Automated Reallocation:</strong> If a claimed lead is
                not funded within 7 business days, it is automatically
                unassigned and reallocated to other eligible regional
                organizations.
              </li>
              <li>
                <strong>3-Business-Day Fallback Expiry:</strong> If no
                organization claims a lead within 3 business days, lead details
                automatically expire.
              </li>
              <li>
                <strong>Strict Privacy Protection:</strong> Lead details are
                restricted exclusively to the assigned organization and the VLF
                administration team.
              </li>
              <li>
                <strong>Final Funding Status:</strong> Once a claimed lead is
                updated to a funded status, its status cannot be reverted to a
                previous or non-funded status.
              </li>
            </ul>
          </div>

          <div class="vlf-footer">
            <span class="vlf-status-info">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              You are currently not enrolled in VLF.
            </span>
            <button
              type="button"
              class="btn-opt-in"
              (click)="optInVlf()"
              [disabled]="isVlfActionLoading"
            >
              Opt In to VLF Programme
            </button>
          </div>
        } @else {
          <div class="vlf-enrolled-banner">
            <div class="vlf-enrolled-left">
              <span class="vlf-badge">Enrolled</span>
              <span class="vlf-active-text"
                >VLF Programme Active — You will receive live VLF lead
                notifications.</span
              >
            </div>

            <div class="vlf-enrolled-actions">
              <button
                type="button"
                class="btn-vlf-info"
                (click)="toggleVlfInfo()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                {{
                  showVlfInfo ? "Hide Rules & Info" : "Programme Rules & Info"
                }}
              </button>

              <button
                type="button"
                class="btn-opt-out"
                (click)="optOutVlf()"
                [disabled]="isVlfActionLoading"
              >
                Opt Out of VLF Programme
              </button>
            </div>
          </div>

          @if (showVlfInfo) {
            <div class="vlf-expanded-info">
              <div class="vlf-body">
                <h5>What is the VLF Programme?</h5>
                <p>
                  The Veteran Loan Fund (VLF) is an automated lead distribution
                  network that delivers high-intent small business leads
                  directly to participating lenders and CDFIs.
                </p>

                <h5>Programme Conditions & How It Works:</h5>
                <ul class="vlf-conditions-list">
                  <li>
                    <strong>First-Come, First-Assigned:</strong> Leads in your
                    location are broadcasted to all participating organizations.
                    The first organization to claim a lead secures exclusive
                    assignment.
                  </li>
                  <li>
                    <strong>Funding Time Frame:</strong> Your organization has 7
                    business days to fund claimed leads and submit weekly status
                    updates.
                  </li>
                  <li>
                    <strong>Automated Reallocation:</strong> If a claimed lead
                    is not funded within 7 business days, it is automatically
                    unassigned and reallocated to other eligible regional
                    organizations.
                  </li>
                  <li>
                    <strong>3-Business-Day Fallback Expiry:</strong> If no
                    organization claims a lead within 3 business days, lead
                    details automatically expire.
                  </li>
                  <li>
                    <strong>Strict Privacy Protection:</strong> Lead details are
                    restricted exclusively to the assigned organization and the
                    VLF administration team.
                  </li>
                  <li>
                    <strong>Final Funding Status:</strong> Once a claimed lead
                    is updated to a funded status, its status cannot be reverted
                    to a previous or non-funded status.
                  </li>
                </ul>
              </div>
            </div>
          }
        }
      </div>
    }
  }
</ng-template>

<ng-template #searchResultsTemplate>
  <div class="search-result-data-wrapper">
    <div class="left-section">
      @if (isSearchResultsLoading) {
        <app-skeleton type="search-results"></app-skeleton>
      } @else if (hasError) {
        <div class="search-result-error-text">
          <span class="text"> Something went wrong! Please try again. </span>
        </div>
      } @else if (searchResultsList && searchResultsList.results.length > 0) {
        <app-search-result-v2
          [searchResultsList]="searchResultsList"
          [selectedTab]="selectedTab?.value ?? ''"
          [selectedSubTab]="selectedSubTab?.value ?? ''"
          [isSearchPage]="isSearchPage"
          [containerSasUrl]="containerSasUrl"
          [currentPage]="currentPage"
          [totalPages]="totalPages"
          [recordId]="recordId"
          (assignedLead)="OnLeadAssign($event)"
          (companyDeleted)="onCompanyDeleted($event)"
          (companyDetailsUpdated)="onCompanyDetailsUpdated($event)"
          (pageChange)="onPageChange($event)"
          (showInMap)="onShowInMap($event)"
          (companyViewed)="onViewDetails($event)"
          (triggerSearchResults)="refreshSearchResults($event)"
        ></app-search-result-v2>
      } @else {
        <ng-container
          *ngTemplateOutlet="emptySearchResultsTemplate"
        ></ng-container>
      }
    </div>

    <ng-container *ngTemplateOutlet="arcgisWebmapTemplate"></ng-container>
  </div>
</ng-template>

<ng-template #arcgisWebmapTemplate>
  <div
    class="right-section"
    [class.hide-map]="
      !isSearchResultsLoading &&
      (!searchResultsList || searchResultsList.results.length === 0)
    "
  >
    @defer (on timer(500ms); prefetch on idle) {
      <app-arcgis-webmap></app-arcgis-webmap>
    } @placeholder {
      <app-skeleton type="arcgis-webmap"></app-skeleton>
    }
  </div>
</ng-template>

<ng-template #emptySearchResultsTemplate>
  <div class="no-data-wrapper">
    <div class="no-data">
      <div class="icon-holder">
        <app-svg-icon
          [iconName]="'no-data.svg'"
          [height]="'100px'"
          [width]="'100px'"
        ></app-svg-icon>
      </div>
      <div class="text-holder">
        @if (selectedTab?.value === LeadTabs.DATA_AXLE) {
          Search Business to View Results.
        } @else {
          No Data Available
        }
      </div>
    </div>
  </div>
</ng-template>

<ng-template #matchedCriteriaTemplate>
  <app-multiselect-filter
    [options]="matchedCriteriaOptions"
    [selectedValues]="matchedCriteriaSelection"
    [placeholder]="'Select criteria'"
    (apply)="onMatchedCriteriaFilterChange($event)"
  ></app-multiselect-filter>
</ng-template>

<ng-template #preLendingFormTemplate>
  <div class="form-container">
    <span class="inline-toggle-label">Pre-lending Form Filled:</span>
    <div class="sub-tabs-left">
      <div class="sub-tabs-list">
        @for (formTab of prelendingFormConfig; track formTab.value) {
          <div class="sub-tab-item">
            <button
              type="button"
              class="sub-tab-button"
              [class.active]="
                preLendingFormSelectedTab?.value === formTab.value
              "
              [disabled]="advanceFilterCount > 0 || isSearchResultsLoading"
              (click)="onPreLendingForm(formTab)"
            >
              <span>{{ formTab.label }}</span>
            </button>

            @if (advanceFilterCount > 0) {
              <div class="custom-tooltip">
                Clear Advanced Filters to access sub tabs.
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>
</ng-template>

<ng-template #inquirySubmissionsTemplate>
  <div class="form-container">
    <div class="sub-tabs-left">
      <div class="sub-tabs-list">
        @for (formTab of inquirySubmissions; track formTab.value) {
          <button
            type="button"
            class="sub-tab-button"
            [class.active]="inquirySelectedTab?.value === formTab.value"
            (click)="onInquirySubmissions(formTab)"
          >
            <span>{{ formTab.label }}</span>
          </button>
        }
      </div>
    </div>
    @if (hasManagePublicLinksPermission || isAdminUser) {
      @if (inquirySelectedTab?.value === LeadSubTabs.BY_LINK) {
        <div class="links-container">
          @if (isAdminUser) {
            <app-searchable-filter
              class="cdfi-filter"
              [filterBy]="deeprootsCdfiFilter"
              [filterByOptions]="deeprootsCdfiOptions"
              [label]="'CDFI:'"
              [placeholder]="'Select CDFI'"
              (filterByChanged)="onDeeprootsCdfiFilterChanged($event)"
            ></app-searchable-filter>
          }

          <app-searchable-filter
            class="cdfi-filter"
            [filterBy]="deeprootsLinkFilter"
            [filterByOptions]="deeprootsLinkOptions"
            [label]="'Link Name:'"
            [placeholder]="'Select Link'"
            [disabled]="isAdminUser && !deeprootsCdfiFilter"
            (filterByChanged)="onDeeprootsLinkFilterChanged($event)"
          ></app-searchable-filter>

          <button
            type="button"
            class="inline-reset-button"
            [disabled]="!deeprootsCdfiFilter && !deeprootsLinkFilter"
            (click)="clearDeeprootsFilters()"
          >
            Reset filters
          </button>
        </div>
      }
    }
    @if (inquirySelectedTab?.value !== LeadSubTabs.BY_LINK) {
      <div class="location-filter-group">
        <div class="links-container">
          <app-searchable-filter
            [filterBy]="stateFilter"
            [filterByOptions]="stateOptions"
            [label]="'State:'"
            [placeholder]="'Select State'"
            (filterByChanged)="onStateFilterChanged($event)"
          ></app-searchable-filter>

          <app-searchable-filter
            [filterBy]="countyFilter"
            [filterByOptions]="countyOptions"
            [label]="'County:'"
            [placeholder]="'Select County'"
            [disabled]="!stateFilter"
            (filterByChanged)="onCountyFilterChanged($event)"
          ></app-searchable-filter>

          <app-searchable-filter
            [filterBy]="cityFilter"
            [filterByOptions]="cityOptions"
            [label]="'City:'"
            [placeholder]="'Select City'"
            [disabled]="!stateFilter"
            (filterByChanged)="onCityFilterChanged($event)"
          ></app-searchable-filter>

          <button
            type="button"
            class="inline-reset-button"
            [disabled]="!stateFilter && !countyFilter && !cityFilter"
            (click)="clearAddressFilters()"
          >
            Clear
          </button>
        </div>
      </div>
    }
  </div>
</ng-template>

<ng-template #searchPageTemplate>
  <div class="search-result-text-wrapper" id="tour-search-page-controls">
    <div class="search-result-text">
      @if (searchResultsList && searchResultsList.results) {
        <span class="text">
          Showing {{ searchResultsList.results.length }} result(s) for
        </span>
      }

      @if (searchResultFilter.sources) {
        @for (source of searchResultFilter.sources.split(","); track $index) {
          <span class="tags" title="Source">
            <span>
              @if (source === "CRF" || source === "WNCSBI_APPS") {
                WNCSBI
              } @else if (source === "CUSTOM_BUSINESS_RECORDS") {
                Referrals
              } @else if (source === "PUBLIC") {
                Inquiries
              } @else {
                {{ source }}
              }
            </span>

            @if (
              searchResultFilter.sources.split(",").length > 1 &&
              !isAdvancedFilterVisible
            ) {
              <a
                href="javascript:void(0)"
                class="close"
                (click)="refreshSource(source)"
              >
              </a>
            }
          </span>
        }
      }

      @if (searchResultFilter.primary_name) {
        <span class="tags" title="Name">
          <span>{{ searchResultFilter.primary_name }}</span>
        </span>
      }
    </div>

    <div class="search-result-text">
      <span class="text"> Total results found: {{ totalResults }} </span>
    </div>
  </div>
  <div>
    @if (isSearchPage) {
      <app-filter
        id="tour-filter-leads-search"
        [filterBy]="filterBy"
        [filterByOptions]="filterRecordsOptions"
        (filterByChanged)="onAllRecordsFilterChange($event)"
      ></app-filter>
    }
  </div>
</ng-template>
